import React, { useMemo } from "react";
import "bootstrap-icons/font/bootstrap-icons.css";

function useScopeId(prefix = "ntf") {
  return useMemo(() => `${prefix}-${Math.random().toString(36).slice(2, 8)}`, []);
}

/**
 * props:
 * - open: boolean  是否显示
 * - onClose: () => void
 * - onMarkAllRead: () => void
 * - items: 通知数组 [{ id, type:'itinerary|booking|community|weather|reward', title, desc, time, unread, actionText, onAction }]
 * - anchorRight: number  右侧距离（默认 16）
 * - anchorTop: number    顶部距离（默认 56）
 */
export default function NotificationPanel({
  open = false,
  onClose,
  onMarkAllRead,
  items = [],
  anchorRight = 16,
  anchorTop = 56,
}) {
  const scope = useScopeId();

  const css = `
  [data-ntf="${scope}"] .layer{
    position:fixed; inset:0; background:transparent;
  }
  [data-ntf="${scope}"] .panel{
    position:fixed; right:${anchorRight}px; top:${anchorTop}px; width:320px; max-height:70vh;
    background:#fff; border:1px solid #e8edf2; border-radius:14px; box-shadow:0 18px 40px rgba(2,6,23,.18);
    overflow:hidden; display:flex; flex-direction:column;
  }
  [data-ntf="${scope}"] .hd{ display:flex; align-items:center; justify-content:space-between; padding:10px 12px; border-bottom:1px solid #eef2f6; }
  [data-ntf="${scope}"] .ttl{ font-weight:800; color:#0f1720; }
  [data-ntf="${scope}"] .link{ color:#2a61ff; cursor:pointer; font-weight:700; }
  [data-ntf="${scope}"] .list{ overflow:auto; padding:8px; display:flex; flex-direction:column; gap:8px; }
  [data-ntf="${scope}"] .card{
    display:flex; gap:10px; padding:10px; border-radius:12px; border:1px solid #eef2f6; background:#fff; position:relative;
    transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
  }
  [data-ntf="${scope}"] .card:hover{ transform:translateY(-1px); background:#f9fbff; }
  [data-ntf="${scope}"] .ic{
    width:28px; height:28px; border-radius:8px; display:flex; align-items:center; justify-content:center; color:#0f2538;
  }
  /* 颜色 */
  [data-ntf="${scope}"] .itinerary{ background:#eef2ff; color:#4361ee; }
  [data-ntf="${scope}"] .booking{ background:#eafaf1; color:#16a34a; }
  [data-ntf="${scope}"] .community{ background:#eef6ff; color:#0ea5e9; }
  [data-ntf="${scope}"] .weather{ background:#f1f5f9; color:#334155; }
  [data-ntf="${scope}"] .reward{ background:#f3f0ff; color:#7c3aed; }

  [data-ntf="${scope}"] .body{ flex:1 1 auto; min-width:0; }
  [data-ntf="${scope}"] .title{ font-weight:800; color:#0f1720; }
  [data-ntf="${scope}"] .desc{ color:#4b5563; font-size:14px; margin-top:2px; }
  [data-ntf="${scope}"] .meta{ color:#64748b; font-size:12px; margin-top:6px; }
  [data-ntf="${scope}"] .act{ color:#2a61ff; font-weight:700; white-space:nowrap; }
  [data-ntf="${scope}"] .dot{
    position:absolute; right:10px; top:10px; width:8px; height:8px; border-radius:50%; background:#2563eb;
  }
  /* 底部关闭 */
  [data-ntf="${scope}"] .ft{ display:flex; justify-content:flex-end; padding:8px; border-top:1px solid #eef2f6; }
  [data-ntf="${scope}"] .btn{ border:1px solid #e6eaf0; background:#fff; padding:6px 10px; border-radius:10px; font-weight:700; }
  `;

  if (!open) return null;

  const iconOf = (type) => {
    switch (type) {
      case "itinerary": return { i: "bi-calendar-check", cls: "itinerary" };
      case "booking":   return { i: "bi-check2-square", cls: "booking" };
      case "community": return { i: "bi-chat-left-dots", cls: "community" };
      case "weather":   return { i: "bi-cloud-drizzle", cls: "weather" };
      case "reward":    return { i: "bi-gift", cls: "reward" };
      default:          return { i: "bi-bell", cls: "" };
    }
  };

  return (
    <div data-ntf={scope} aria-live="polite" aria-label="Notifications">
      <style>{css}</style>
      {/* 点击遮罩关闭 */}
      <div className="layer" onClick={onClose} />
      <div className="panel" role="dialog" aria-modal="true">
        <div className="hd">
          <div className="ttl">Message Notifications</div>
          <div className="d-flex align-items-center gap-3">
            <span className="link" onClick={onMarkAllRead}>Mark All as Read</span>
            <i className="bi bi-x" role="button" onClick={onClose} />
          </div>
        </div>

        <div className="list">
          {items.map((n) => {
            const ic = iconOf(n.type);
            return (
              <div key={n.id} className="card">
                <div className={`ic ${ic.cls}`}><i className={`bi ${ic.i}`} /></div>
                <div className="body">
                  <div className="title">{n.title}</div>
                  <div className="desc">{n.desc}</div>
                  <div className="d-flex align-items-center justify-content-between mt-1">
                    <div className="meta">{n.time}</div>
                    {n.actionText && (
                      <span className="act" role="button" onClick={() => n.onAction?.(n)}>
                        {n.actionText}
                      </span>
                    )}
                  </div>
                </div>
                {n.unread ? <span className="dot" /> : null}
              </div>
            );
          })}
        </div>

        <div className="ft">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
